import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../store/hooks';
import { requestPasswordReset } from '../../store/slices/authSlice';
import { Link } from 'react-router-dom';

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  padding: 2rem 1rem;
  padding-top: calc(70px + 2rem);

  @media (min-width: 769px) {
    padding-top: 80px;
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 520px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.25);
  padding: 1.5rem;
`;

const Title = styled.h2`
  margin: 0 0 0.25rem 0;
  text-align: center;
`;

const SubTitle = styled.p`
  margin: 0 0 1rem 0;
  text-align: center;
  color: #374151;
`;

const Form = styled.form`
  display: grid;
  gap: 0.75rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const Input = styled.input<{ invalid?: boolean }>`
  width: 100%;
  padding: 0.65rem 0.75rem;
  border-radius: 10px;
  border: 1px solid ${p => (p.invalid ? '#ef4444' : 'rgba(0,0,0,0.15)')};
  outline: none;
  &:focus { border-color: var(--primary-blue); box-shadow: 0 0 0 3px rgba(59,130,246,0.25); }
`;

const Submit = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--primary-orange) 0%, var(--primary-blue) 100%);
  color: white;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: filter .2s ease;
  &:disabled { opacity: .6; cursor: not-allowed; }
  &:hover:not(:disabled) { filter: brightness(1.02); }
`;

const Help = styled.div`
  text-align: center;
  margin-top: 0.75rem;
`;

const Success = styled.div`
  margin-top: 0.75rem;
  background: #ecfdf5;
  color: #065f46;
  border: 1px solid #a7f3d0;
  padding: 0.75rem;
  border-radius: 10px;
`;

function isEmail(v: string) {
  return /[^@\s]+@[^@\s]+\.[^@\s]+/.test(v);
}

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const usernameInvalid = useMemo(() => username.trim().length < 2, [username]);
  const emailInvalid = useMemo(() => !isEmail(email), [email]);
  const canSubmit = !usernameInvalid && !emailInvalid && !submitting;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setSuccessMsg(null);
    try {
      const action: any = await dispatch(requestPasswordReset({ username: username.trim(), email: email.trim() }));
      if (action?.type?.endsWith('/fulfilled')) {
        const message = action?.payload?.message || t('portal.login.forgot_password_success_default', 'If an account exists for that username and email, we’ve sent password reset instructions.');
        setSuccessMsg(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Wrapper>
      <Card>
        <Title>{t('portal.login.forgot_password_title', 'Forgot your password?')}</Title>
        <SubTitle>{t('portal.login.forgot_password_subtitle', 'Enter your username and email to receive a reset link.')}</SubTitle>
        <Form onSubmit={onSubmit} noValidate>
          <div>
            <Label htmlFor="fp-username">{t('portal.login.username', 'Username')}</Label>
            <Input id="fp-username" name="username" value={username} onChange={(e) => setUsername(e.target.value)} invalid={username !== '' && usernameInvalid} autoComplete="username" />
          </div>
          <div>
            <Label htmlFor="fp-email">{t('portal.login.email', 'Email')}</Label>
            <Input id="fp-email" name="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} invalid={email !== '' && emailInvalid} autoComplete="email" />
          </div>
          <Submit type="submit" disabled={!canSubmit}>{submitting ? (t('portal.common.sending', 'Sending…') as string) : (t('portal.login.send_reset_link', 'Send reset link') as string)}</Submit>
        </Form>
        {successMsg && (
          <Success role="status" aria-live="polite">{successMsg}</Success>
        )}
        <Help>
          <Link to="/portal/login">{t('portal.login.back_to_login', 'Back to login')}</Link>
        </Help>
      </Card>
    </Wrapper>
  );
};

export default ForgotPassword;
